import { useEffect, useRef, useState } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { api } from '../api.js';
import { t } from '../i18n.js';

type Range = '24h' | '7d' | '30d' | 'all';

const RANGES: { key: Range; labelKey: string; seconds: number; resolution: 'minute' | 'hour' | 'day' }[] = [
  { key: '24h', labelKey: 'chart.range_24h', seconds: 24 * 3600, resolution: 'minute' },
  { key: '7d', labelKey: 'chart.range_7d', seconds: 7 * 86400, resolution: 'hour' },
  { key: '30d', labelKey: 'chart.range_30d', seconds: 30 * 86400, resolution: 'hour' },
  { key: 'all', labelKey: 'chart.range_all', seconds: 365 * 86400, resolution: 'day' },
];

function pad2(n: number): string { return n.toString().padStart(2, '0'); }

function formatDDMM(d: Date): string {
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
}

function formatTick(unixSec: number, incrSec: number): string {
  const d = new Date(unixSec * 1000);
  const isDayBoundary = d.getHours() === 0 && d.getMinutes() === 0;
  const isYearBoundary = isDayBoundary && d.getMonth() === 0 && d.getDate() === 1;

  if (incrSec >= 86400) {
    return isYearBoundary ? d.getFullYear().toString() : formatDDMM(d);
  }

  const time = incrSec >= 3600
    ? `${pad2(d.getHours())}h`
    : `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

  if (isDayBoundary) return `${time}\n${formatDDMM(d)}`;
  return time;
}

type HistoryPoint = {
  ts: number;
  current_temp: number | null;
  target_temp: number | null;
  outside_temp: number | null;
  heating_power: number | null;
};

const COLOURS = {
  axis: '#777',
  grid: '#f0f0f0',
  indoor: '#e55f1c',
  target: '#bbbbbb',
  outside: '#5a8ec0',
  heating: 'rgba(229,95,28,0.32)',
  heatingFill: 'rgba(229,95,28,0.16)',
};

export function HistoryChart() {
  const ref = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  const [range, setRange] = useState<Range>('24h');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const mount = ref.current;
    if (!mount) return;

    const r = RANGES.find((x) => x.key === range)!;
    const to = Math.floor(Date.now() / 1000);
    const from = to - r.seconds;
    let cancelled = false;

    const heightFor = (wide: boolean) =>
      wide ? Math.max(420, Math.min(720, window.innerHeight - 180)) : 380;

    let lastPoints: HistoryPoint[] | null = null;
    let lastWide = window.innerWidth >= 720;

    const buildPlot = (points: HistoryPoint[]) => {
      const ts = points.map((p) => p.ts);
      const inside = points.map((p) => p.current_temp);
      const target = points.map((p) => p.target_temp);
      const outside = points.map((p) => p.outside_temp);
      const heating = points.map((p) => p.heating_power);
      const data: uPlot.AlignedData = [ts, inside, target, outside, heating] as unknown as uPlot.AlignedData;
      plotRef.current?.destroy();
      const axisOpts: uPlot.Axis = {
        stroke: COLOURS.axis,
        grid: { stroke: COLOURS.grid, width: 1 },
        ticks: { stroke: COLOURS.grid, width: 1 },
        space: 50,
      };
      // 24h time, DD/MM dates. Multi-line strings render two-line labels at
      // day/year boundaries (uPlot splits on \n).
      const timeAxisValues: uPlot.Axis.Values = (_u, splits, _axisIdx, _foundSpace, foundIncr) =>
        splits.map((s) => formatTick(s, foundIncr));
      // On wide viewports give the chart a generous height; on phones, keep it tall enough to read.
      const isWide = typeof window !== 'undefined' && window.innerWidth >= 720;
      const height = heightFor(isWide);
      const indoorWidth = isWide ? 2.4 : 1.8;
      const outsideWidth = isWide ? 2 : 1.5;
      const axes: uPlot.Axis[] = [
        { ...axisOpts, values: timeAxisValues },
        { ...axisOpts, scale: 'temp', values: (_u, vals) => vals.map((v) => v + '°') },
      ];
      if (isWide) {
        axes.push({ ...axisOpts, side: 1, scale: 'pct', values: (_u, vals) => vals.map((v) => v + '%') });
      }
      const opts: uPlot.Options = {
        width: mount.clientWidth,
        height,
        scales: {
          x: { time: true, range: [from, to] },
          temp: { auto: true },
          pct: { range: [0, 100] },
        },
        series: [
          {},
          { label: t('chart.indoor'), stroke: COLOURS.indoor, width: indoorWidth, scale: 'temp' },
          { label: t('chart.target'), stroke: COLOURS.target, width: 1, dash: [4, 4], scale: 'temp' },
          { label: t('chart.outside'), stroke: COLOURS.outside, width: outsideWidth, scale: 'temp' },
          { label: t('chart.heating'), stroke: COLOURS.heating, fill: COLOURS.heatingFill, width: 1, scale: 'pct' },
        ],
        axes,
      };
      plotRef.current = new uPlot(opts, data, mount);
      lastPoints = points;
      lastWide = isWide;
    };

    void api.getHistory(from, to, r.resolution)
      .then((res) => {
        if (cancelled) return;
        buildPlot(res.points);
        setErr(null);
      })
      .catch((e: Error) => { if (!cancelled) setErr(e.message); });

    const ro = new ResizeObserver(() => {
      const isWide = window.innerWidth >= 720;
      if (isWide !== lastWide && lastPoints) {
        lastWide = isWide;
        buildPlot(lastPoints);
        return;
      }
      const p = plotRef.current;
      if (!p) return;
      p.setSize({ width: mount.clientWidth, height: heightFor(isWide) });
    });
    ro.observe(mount);

    return () => {
      cancelled = true;
      ro.disconnect();
      plotRef.current?.destroy();
      plotRef.current = null;
    };
  }, [range]);

  return (
    <div className="chart-card">
      <div className="chart-head">
        <div className="chart-title">{t('chart.title')}</div>
        <div className="chart-pills">
          {RANGES.map((r) => (
            <button
              key={r.key}
              className={`chart-pill${r.key === range ? ' is-active' : ''}`}
              onClick={() => setRange(r.key)}
            >{t(r.labelKey)}</button>
          ))}
        </div>
      </div>
      <div className="chart-mount" ref={ref} />
      {err && <div className="muted" style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>{err}</div>}
    </div>
  );
}
